/**
 * Betting Flow Section
 *
 * Displays public betting percentages, sharp money indicators,
 * and bookmaker breakdown for an event.
 */

import type { BettingVolumeResponse } from '../api/client';

interface Props {
  data: BettingVolumeResponse;
  homeTeam: string;
  awayTeam: string;
}

export default function BettingFlowSection({ data, homeTeam, awayTeam }: Props) {
  const { publicBetting, markets, summary, lineMovement } = data;

  // Format American odds
  const formatAmericanOdds = (price: number) => {
    if (price >= 2) {
      return `+${Math.round((price - 1) * 100)}`;
    }
    return `-${Math.round(100 / (price - 1))}`;
  };

  // Get short team name (last word)
  const getShortName = (name: string) => {
    const parts = name.split(' ');
    return parts[parts.length - 1];
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">BETTING FLOW</h2>
          <p className="text-sm text-gray-400 mt-1">
            Public betting percentages and money flow analysis
          </p>
        </div>
        {publicBetting && (
          <div className="text-xs text-gray-500">
            Source: {publicBetting.source} | Updated: {new Date(publicBetting.lastUpdate).toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Public Betting Percentages */}
      {publicBetting && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
            Public Betting Split
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ticket Percentage */}
            <div className="bg-surface-50 rounded-lg p-4 border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Tickets (# of bets)</span>
              </div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-blue-400 font-medium">{getShortName(homeTeam)}</span>
                <span className="text-orange-400 font-medium">{getShortName(awayTeam)}</span>
              </div>
              <div className="h-4 bg-gray-700 rounded-full overflow-hidden flex">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${publicBetting.ticketPctHome}%` }}
                />
                <div
                  className="h-full bg-orange-500 transition-all"
                  style={{ width: `${publicBetting.ticketPctAway}%` }}
                />
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-blue-400">{publicBetting.ticketPctHome.toFixed(1)}%</span>
                <span className="text-orange-400">{publicBetting.ticketPctAway.toFixed(1)}%</span>
              </div>
            </div>

            {/* Money Percentage */}
            <div className="bg-surface-50 rounded-lg p-4 border border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Money ($ wagered)</span>
              </div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-blue-400 font-medium">{getShortName(homeTeam)}</span>
                <span className="text-orange-400 font-medium">{getShortName(awayTeam)}</span>
              </div>
              <div className="h-4 bg-gray-700 rounded-full overflow-hidden flex">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${publicBetting.moneyPctHome}%` }}
                />
                <div
                  className="h-full bg-orange-500 transition-all"
                  style={{ width: `${publicBetting.moneyPctAway}%` }}
                />
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-blue-400">{publicBetting.moneyPctHome.toFixed(1)}%</span>
                <span className="text-orange-400">{publicBetting.moneyPctAway.toFixed(1)}%</span>
              </div>
            </div>
          </div>

          {/* Sharp Money Indicator */}
          <div className="mt-4 bg-surface-50 rounded-lg p-4 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-400">Sharp Money Indicator</span>
                <p className="text-xs text-gray-500 mt-1">
                  Divergence between money% and ticket% • Positive = sharp on home
                </p>
              </div>
              <div className="text-right">
                <span className={`text-2xl font-bold font-mono ${
                  Math.abs(publicBetting.sharpMoneyIndicator) >= 10
                    ? publicBetting.sharpMoneyIndicator > 0 ? 'text-blue-400' : 'text-orange-400'
                    : 'text-gray-400'
                }`}>
                  {publicBetting.sharpMoneyIndicator > 0 ? '+' : ''}{publicBetting.sharpMoneyIndicator.toFixed(1)}%
                </span>
                {Math.abs(publicBetting.sharpMoneyIndicator) >= 10 && (
                  <span className="block text-xs text-yellow-400 mt-1">
                    RLM Signal: Sharp money on {publicBetting.sharpMoneyIndicator > 0 ? getShortName(homeTeam) : getShortName(awayTeam)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bookmaker Summary */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
          Bookmaker Coverage
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface-50 rounded-lg p-3 border border-gray-800 text-center">
            <div className="text-2xl font-bold text-white">{summary.totalBookmakers}</div>
            <div className="text-xs text-gray-400">Total Books</div>
          </div>
          <div className="bg-surface-50 rounded-lg p-3 border border-purple-500/30 text-center">
            <div className="text-2xl font-bold text-purple-400">{summary.sharpBookCount}</div>
            <div className="text-xs text-gray-400">Sharp Books</div>
          </div>
          <div className="bg-surface-50 rounded-lg p-3 border border-orange-500/30 text-center">
            <div className="text-2xl font-bold text-orange-400">{summary.squareBookCount}</div>
            <div className="text-xs text-gray-400">Square Books</div>
          </div>
          <div className="bg-surface-50 rounded-lg p-3 border border-gray-800 text-center">
            <div className="text-2xl font-bold text-white">{Object.keys(summary.marketCoverage).length}</div>
            <div className="text-xs text-gray-400">Market Types</div>
          </div>
        </div>

        {/* Sharp vs Square Divergence */}
        {summary.sharpVsSquareDivergence && (
          <div className="mt-4 bg-surface-50 rounded-lg p-4 border border-gray-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Sharp vs Square Price Divergence (H2H)</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <span className="text-blue-400 text-sm">{getShortName(homeTeam)}</span>
                <div className={`text-lg font-mono font-bold ${
                  Math.abs(summary.sharpVsSquareDivergence.home) > 0.1 ? 'text-yellow-400' : 'text-gray-300'
                }`}>
                  {summary.sharpVsSquareDivergence.home > 0 ? '+' : ''}{summary.sharpVsSquareDivergence.home.toFixed(2)}
                </div>
              </div>
              <div className="text-center">
                <span className="text-orange-400 text-sm">{getShortName(awayTeam)}</span>
                <div className={`text-lg font-mono font-bold ${
                  Math.abs(summary.sharpVsSquareDivergence.away) > 0.1 ? 'text-yellow-400' : 'text-gray-300'
                }`}>
                  {summary.sharpVsSquareDivergence.away > 0 ? '+' : ''}{summary.sharpVsSquareDivergence.away.toFixed(2)}
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Positive = sharps offering better odds than squares
            </p>
          </div>
        )}
      </div>

      {/* Line Movement */}
      {lineMovement.openingLine && lineMovement.currentLine && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
            Line Movement
          </h3>

          <div className="bg-surface-50 rounded-lg p-4 border border-gray-800">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xs text-gray-500 mb-1">Opening</div>
                <div className="text-sm">
                  <span className="text-blue-400">{formatAmericanOdds(lineMovement.openingLine.home)}</span>
                  <span className="text-gray-500 mx-2">/</span>
                  <span className="text-orange-400">{formatAmericanOdds(lineMovement.openingLine.away)}</span>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Current</div>
                <div className="text-sm">
                  <span className="text-blue-400">{formatAmericanOdds(lineMovement.currentLine.home)}</span>
                  <span className="text-gray-500 mx-2">/</span>
                  <span className="text-orange-400">{formatAmericanOdds(lineMovement.currentLine.away)}</span>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Movement</div>
                {lineMovement.movement && (
                  <div className="text-sm">
                    <span className={lineMovement.movement.home > 0 ? 'text-green-400' : lineMovement.movement.home < 0 ? 'text-red-400' : 'text-gray-400'}>
                      {lineMovement.movement.home > 0 ? '+' : ''}{lineMovement.movement.home.toFixed(2)}
                    </span>
                    <span className="text-gray-500 mx-2">/</span>
                    <span className={lineMovement.movement.away > 0 ? 'text-green-400' : lineMovement.movement.away < 0 ? 'text-red-400' : 'text-gray-400'}>
                      {lineMovement.movement.away > 0 ? '+' : ''}{lineMovement.movement.away.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Market Breakdown */}
      <div>
        <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
          Bookmaker Detail by Market
        </h3>

        <div className="space-y-4">
          {markets.map(market => {
            const marketLabels: Record<string, { label: string; icon: string }> = {
              h2h: { label: 'Moneyline', icon: '🎯' },
              spreads: { label: 'Spread', icon: '📊' },
              totals: { label: 'Total', icon: '📈' },
            };
            const { label, icon } = marketLabels[market.marketType] || { label: market.marketType, icon: '📋' };

            return (
              <div key={market.marketType} className="bg-surface-50 rounded-lg p-4 border border-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span>{icon}</span>
                    <span className="font-semibold">{label}</span>
                  </div>
                  <span className="text-sm text-gray-400">{market.bookmakerCount} books</span>
                </div>

                {market.outcomes.slice(0, 6).map(outcome => (
                  <div key={`${outcome.name}-${outcome.point || ''}`} className="mb-3 last:mb-0">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-300">
                        {outcome.point !== null && outcome.point !== undefined
                          ? `${outcome.name} ${outcome.point > 0 ? '+' : ''}${outcome.point}`
                          : outcome.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        Best: <span className="text-green-400 font-mono">{formatAmericanOdds(outcome.bestOdds)}</span>
                        {' @ '}
                        <span className={outcome.bookmakers.find(b => b.name === outcome.bestBook)?.isSharp ? 'text-purple-400' : 'text-gray-400'}>
                          {outcome.bestBook}
                        </span>
                      </span>
                    </div>

                    {/* Bookmaker prices breakdown */}
                    <div className="flex flex-wrap gap-1">
                      {outcome.bookmakers.slice(0, 8).map((bookie, idx) => (
                        <span
                          key={`${bookie.name}-${idx}`}
                          className={`text-xs px-2 py-0.5 rounded ${
                            bookie.isSharp
                              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                              : 'bg-gray-700/50 text-gray-400 border border-gray-600/30'
                          }`}
                          title={`${bookie.name}: ${formatAmericanOdds(bookie.price)}`}
                        >
                          {bookie.name.slice(0, 8)}: {formatAmericanOdds(bookie.price)}
                        </span>
                      ))}
                      {outcome.bookmakers.length > 8 && (
                        <span className="text-xs text-gray-500 px-2 py-0.5">
                          +{outcome.bookmakers.length - 8} more
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* No data message */}
      {!publicBetting && markets.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          No betting flow data available for this event
        </div>
      )}
    </div>
  );
}
